import { create } from 'zustand';
import { api, type Pin, type PinKind } from '@/services/api';

interface PinsState {
  pins: Pin[];
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  load: () => Promise<void>;
  add: (kind: PinKind, entityId: string) => Promise<void>;
  remove: (kind: PinKind, entityId: string) => Promise<void>;
  isPinned: (kind: PinKind, entityId: string) => boolean;
}

export const usePinsStore = create<PinsState>((set, get) => ({
  pins: [],
  isLoaded: false,
  isLoading: false,
  error: null,

  load: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const { pins } = await api.pins.list();
      set({ pins, isLoaded: true, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load pins',
      });
    }
  },

  add: async (kind, entityId) => {
    if (get().isPinned(kind, entityId)) return;
    try {
      await api.pins.add(kind, entityId);
      // Refetch to pull the server-side label/sublabel metadata.
      await get().load();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to pin' });
      throw err;
    }
  },

  remove: async (kind, entityId) => {
    if (!get().isPinned(kind, entityId)) return;
    try {
      await api.pins.remove(kind, entityId);
      set((state) => ({
        pins: state.pins.filter((p) => !(p.kind === kind && p.entityId === entityId)),
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to unpin' });
      throw err;
    }
  },

  isPinned: (kind, entityId) =>
    get().pins.some((p) => p.kind === kind && p.entityId === entityId),
}));
